import * as React from 'react'
import styled from 'styled-components'

// Import Highcharts
import Highcharts from 'highcharts'
import Dumbbel from 'highcharts/modules/dumbbell'
import LollipopHC from 'highcharts/modules/lollipop'

// init the module
Dumbbel(Highcharts)
LollipopHC(Highcharts)

/* This component should receive a ref to bind the chart to. The data should be set by the parent component
 using either highcharts setData or addPoint */

type LollipopProps = {
    chart: React.ElementRef<Highcharts.Chart>
    width: number
    height: number
}

const LollipopChart = (props: LollipopProps) => {
    const { chart, width, height } = props
    const higchartsDOMEl = React.useRef(null)
    const loading = React.useRef(true)
    const options = React.useMemo(() => {
        const options = {
            chart: {
                type: 'lollipop',
                events: {
                    redraw: () => {
                        if (loading.current) {
                            loading.current = false
                            chart.current.hideLoading()
                        }
                    }
                },
                backgroundColor: 'transparent',
                spacingBottom: 10,
                spacingTop: 10,
                spacingLeft: 10,
                spacingRight: 10
            },
            title: {
                text: undefined
            },    
            plotOptions: {
                series: {
                    animation: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    },
                    marker: {
                        radius: 10
                    }
                }
            },
            legend: {},
            xAxis: {
                type: 'datetime'
            }
        }

        return options
    }, [chart])

    /*
        Chart is created here, but it still has no data.
        It will be rendered and show a loading message until it is redrawn with the data.
    */

    React.useEffect(() => {
        chart.current = Highcharts.chart(higchartsDOMEl.current, options)

        return () => {
            chart.current.destroy()
            chart.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, higchartsDOMEl])

    React.useEffect(() => {
        if (chart.current) {
            chart.current.update(options)
        }
    }, [chart, options])

    React.useEffect(() => {
        chart.current.reflow()
    }, [width, height, chart])

    return (
        <div style={{ display: 'flex', height: '100%', width: '100%' }}>
            <HighchartsContainer ref={higchartsDOMEl} />
        </div>
    )
}

export default React.memo<LollipopProps>(LollipopChart)

const HighchartsContainer = styled.div`
    height: 100%;
    width: 100%;
`

